import { supabase, isSupabaseConfigured } from './supabase';

/**
 * Supabase-backed storage service that mirrors the localStorage API.
 * Uses a single `planner_store` table with key-value pairs scoped by user_id.
 * Falls back to localStorage if Supabase is not configured.
 */

// ─── Local Storage Fallback ──────────────────────────────────────────────────
export const localStorageFallback = {
    get: async (key) => {
        const value = localStorage.getItem(key);
        return value ? { key, value } : null;
    },
    set: async (key, value) => {
        localStorage.setItem(key, value);
        return { key, value };
    },
    delete: async (key) => {
        localStorage.removeItem(key);
        return { key, deleted: true };
    },
    list: async (prefix) => {
        const keys = Object.keys(localStorage).filter(k =>
            prefix ? k.startsWith(prefix) : true
        );
        return { keys, prefix };
    }
};

// ─── User-Scoped Supabase Storage Factory ───────────────────────────────────
/**
 * Creates a storage instance scoped to a specific user.
 * All queries include user_id for RLS-compatible data isolation.
 */
export function createUserStorage(userId) {
    if (!isSupabaseConfigured() || !userId) {
        console.warn('createUserStorage: Supabase not configured or no userId, using localStorage');
        return localStorageFallback;
    }

    const cacheKey = (key) => `${userId}:${key}`;

    return {
        get: async (key) => {
            try {
                const { data, error } = await supabase
                    .from('planner_store')
                    .select('value')
                    .eq('key', key)
                    .eq('user_id', userId)
                    .maybeSingle();

                if (error) {
                    console.error(`Supabase GET error for "${key}":`, error.message);
                    const cached = localStorage.getItem(cacheKey(key));
                    return cached ? { key, value: cached } : null;
                }

                if (data) {
                    localStorage.setItem(cacheKey(key), data.value);
                    return { key, value: data.value };
                }

                const cached = localStorage.getItem(cacheKey(key));
                return cached ? { key, value: cached } : null;
            } catch (err) {
                console.error(`Supabase GET exception for "${key}":`, err);
                const cached = localStorage.getItem(cacheKey(key));
                return cached ? { key, value: cached } : null;
            }
        },

        set: async (key, value) => {
            try {
                localStorage.setItem(cacheKey(key), value);

                const { error } = await supabase
                    .from('planner_store')
                    .upsert(
                        {
                            key,
                            value,
                            user_id: userId,
                            updated_at: new Date().toISOString()
                        },
                        { onConflict: 'user_id,key' }
                    );

                if (error) {
                    console.error(`Supabase SET error for "${key}":`, error.message);
                }

                return { key, value };
            } catch (err) {
                console.error(`Supabase SET exception for "${key}":`, err);
                return { key, value };
            }
        },

        delete: async (key) => {
            try {
                localStorage.removeItem(cacheKey(key));

                const { error } = await supabase
                    .from('planner_store')
                    .delete()
                    .eq('key', key)
                    .eq('user_id', userId);

                if (error) {
                    console.error(`Supabase DELETE error for "${key}":`, error.message);
                }

                return { key, deleted: true };
            } catch (err) {
                console.error(`Supabase DELETE exception for "${key}":`, err);
                return { key, deleted: true };
            }
        },

        list: async (prefix) => {
            try {
                let query = supabase
                    .from('planner_store')
                    .select('key')
                    .eq('user_id', userId);

                if (prefix) {
                    query = query.like('key', `${prefix}%`);
                }

                const { data, error } = await query;

                if (error) {
                    console.error('Supabase LIST error:', error.message);
                    return { keys: [], prefix };
                }

                const keys = data.map(row => row.key);
                return { keys, prefix };
            } catch (err) {
                console.error('Supabase LIST exception:', err);
                return { keys: [], prefix };
            }
        }
    };
}

// ─── Default export (localStorage fallback for initial load) ────────────────
const storage = isSupabaseConfigured() ? localStorageFallback : localStorageFallback;

export default storage;
